import React, { createContext, useCallback, useContext } from 'react';
import type { ButtonProps } from '@fluentui/react-components';
import { Button } from '@fluentui/react-components';
import { useOptionalAuth } from '../../contexts/AuthContext';

interface MutationOnlyProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface WorkspaceAccess {
  isReadOnly: boolean;
  runMutation: <T>(mutation: () => T) => T | undefined;
}

export type DashboardAccessMode = 'owner' | 'shared-readonly' | 'embedded-readonly';
const DashboardAccessModeContext = createContext<DashboardAccessMode>('owner');

export const DashboardAccessModeProvider: React.FC<{
  mode: DashboardAccessMode;
  children: React.ReactNode;
}> = ({ mode, children }) => (
  <DashboardAccessModeContext.Provider value={mode}>
    {children}
  </DashboardAccessModeContext.Provider>
);

/**
 * Semantic workspace access policy.
 *
 * Product components should depend on this policy instead of knowing why the
 * workspace is read-only. The API remains the authoritative security boundary.
 */
export function useWorkspaceAccess(): WorkspaceAccess {
  const isImpersonating = useOptionalAuth()?.isImpersonating ?? false;
  const accessMode = useContext(DashboardAccessModeContext);
  const isReadOnly = isImpersonating || accessMode !== 'owner';

  const runMutation = useCallback(
    <T, >(mutation: () => T): T | undefined => {
      if (isReadOnly) return undefined;
      return mutation();
    },
    [isReadOnly],
  );

  return { isReadOnly, runMutation };
}

/** Button for write operations; automatically disabled in read-only sessions. */
export const MutationButton: React.FC<ButtonProps> = ({
  disabled,
  ...props
}) => {
  const { isReadOnly } = useWorkspaceAccess();

  return <Button {...props} disabled={disabled || isReadOnly} />;
};

/** Renders mutation-only UI only when the workspace allows writes. */
export const MutationOnly: React.FC<MutationOnlyProps> = ({
  children,
  fallback = null,
}) => {
  const { isReadOnly } = useWorkspaceAccess();
  return <>{isReadOnly ? fallback : children}</>;
};